import { Button, Typography, Box } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { GeneralConstants } from 'app/constants/general-constants';
import { Store } from 'app/model/cris/store';
import { createNewKeyword, StoreKeyword } from 'app/model/cris/store-keyword';
import { keywordService } from 'app/services/store/keyword.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

interface StoreKeywordGridProps {
    store: Store;
}

const sortRowsById = (inputRows: StoreKeyword[]): StoreKeyword[] => {
    return inputRows.sort((a, b) => (a.id as unknown as number) - (b.id as unknown as number));
};

export const StoreKeywordGrid: React.FunctionComponent<StoreKeywordGridProps> = ({ store }) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<StoreKeyword[]>([]);

    const columns: Array<GridColDef<StoreKeyword>> = [
        {
            field: 'id',
            headerName: 'ID',
            width: 150,
            disableColumnMenu: true,
        },
        {
            field: 'keyword',
            headerName: 'Keyword',
            width: 250,
            type: 'string',
            editable: true,
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 70,
            type: 'boolean',
            editable: true,
        },
        {
            field: 'nameKeyword',
            headerName: 'Main Keyword',
            width: 120,
            type: 'boolean',
            editable: true,
        },
        {
            field: 'dateCreated',
            headerName: 'Created',
            width: 200,
            valueFormatter: DataGridFormatters.dateTimeFormatter,
        },
        {
            field: 'dateEdited',
            headerName: 'Modified',
            width: 200,
            valueFormatter: DataGridFormatters.dateTimeFormatter,
        },
    ];

    const createKeyword = useCallback(() => {
        keywordService.save(createNewKeyword(), store).subscribe((created) => {
            setRows((prev) => [...prev, created]);
        });
    }, [store]);

    const handleDataChange = async (newRow: StoreKeyword, originalRow: StoreKeyword): Promise<StoreKeyword> => {
        const obs = keywordService.patch(
            newRow.id,
            {
                ...(newRow.keyword !== originalRow.keyword && { keyword: newRow.keyword }),
                ...(newRow.active !== originalRow.active && { active: newRow.active }),
                ...(newRow.nameKeyword !== originalRow.nameKeyword && { nameKeyword: newRow.nameKeyword }),
            },
            store,
        );
        obs.subscribe((updated) => {
            setRows((prev) => {
                const updatedRows = prev.map((row) => (row.id === newRow.id ? { ...row, ...updated } : row));

                if (newRow.nameKeyword !== originalRow.nameKeyword) {
                    keywordService
                        .listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE }, store)
                        .subscribe((freshRows: StoreKeyword[]) => {
                            const freshRowMap = new Map(freshRows.map((row) => [row.id, row]));
                            setRows(sortRowsById(updatedRows.map((row) => freshRowMap.get(row.id) ?? row)));
                        });
                } else {
                    return sortRowsById(updatedRows);
                }

                return updatedRows;
            });
        });

        return firstValueFrom(obs);
    };

    useEffect(() => {
        setLoading(true);
        keywordService
            .listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE }, store)
            .pipe(finalize(() => setLoading(false)))
            .subscribe(setRows);
    }, [store]);

    return (
        <>
            <Typography>Double click on keyword/active value to edit it (or select cell and press enter)</Typography>
            <Button type='button' onClick={createKeyword}>
                Add Keyword
            </Button>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    density='compact'
                    loading={loading}
                    columns={columns}
                    rows={rows}
                    pagination
                    autoHeight
                    processRowUpdate={handleDataChange}
                />
            </Box>
        </>
    );
};
